import {
  tryUseNuxtApp,
  useHead,
  useHeadSafe,
  useSeoMeta,
  useNuxtApp,
  useRuntimeConfig,
  useRoute,
  useFetch,
  useRequestHeaders,
  useRequestEvent,
  setResponseStatus,
} from '#imports';
import { parseVendorDescription, generateVendorJsonLd } from '~/utils/vendorMeta';
import { escapeJsonLd, listingCanonical, robotsContent } from '~/utils/seoHead';

export function convertStylesheetResponse(enqueuedStylesheets) {
  const loadStylesheets = [];

  if (enqueuedStylesheets) {
    enqueuedStylesheets.nodes.forEach(stylesheet => {
      if (!stylesheet.src) {
        return;
      }

      loadStylesheets.push({
        href: stylesheet.src,
        rel: 'stylesheet',
        id: stylesheet.id
      });
    });
  }

  return loadStylesheets;
}

export async function getPageContentFromUri(uriOverride = null) {
  return new Promise(async (resolve, reject) => {
    const app = useNuxtApp();
    const route = useRoute();
    const config = useRuntimeConfig();
    const requestHeaders = useRequestHeaders(['cookie']);
    let uri = uriOverride ?? (route.path || '/');
    let params = '';

    if (route.query.preview) {
      params = '&preview=true'
    }

    await useFetch(`${config.public.baseUrl}/wp-json/content-search/v1/search?type=content&uri=${uri}${params}`, {
      method: 'GET',
      headers: {
        ...requestHeaders
      },
      server: true,
    }).then((response) => {
      const pageData = response?.data?.value?.nodeByUri;

      if (pageData) {
        app.runWithContext(() => {
          const seo = pageData.seo;

          if (seo) {
            const canonical = seo.canonical || listingCanonical(config.public.baseUrl, uri);

            useHead({
              title: seo.title,
              link: [
                ...convertStylesheetResponse(pageData.enqueuedStylesheets),
                { rel: 'canonical', href: canonical, key: 'canonical' },
              ],
              script: seo.schema?.raw ? [{
                id: 'json-ld',
                type: 'application/ld+json',
                innerHTML: escapeJsonLd(seo.schema.raw),
              }] : [],
            });
            useSeoMeta({
              title: seo.title,
              ogTitle: seo.title,
              twitterTitle: seo.title,
              description: seo.metaDesc,
              ogDescription: seo.metaDesc,
              twitterDescription: seo.metaDesc,
              twitterCard: 'summary_large_image',
              author: pageData.author?.node?.name,
              ogType: seo.opengraphType,
              ogUrl: canonical,
              robots: robotsContent(seo),
              ogLocale: 'en_US',
              ogSiteName: seo.opengraphSiteName,
              twitterSite: seo.opengraphSiteName,
              publisher: seo.opengraphPublisher,
              articlePublishedTime: seo.opengraphPublishTime,
              ogImage: {
                url: seo.opengraphImage?.mediaItemUrl,
                alt: seo.opengraphImage?.altText,
                secureUrl: seo.opengraphImage?.mediaItemUrl,
              },
              twitterImage: seo.opengraphImage?.mediaItemUrl,
              twitterImageAlt: seo.opengraphImage?.altText,
            });
          }
        });
      }

      resolve({
        data: pageData,
        allData: response,
        // Fetch failed (5xx, timeout): callers must not treat this as "page does not exist"
        failed: !! response?.error?.value,
      });
    }).catch((e) => {
      reject(e);
    });
  });
}

/**
 * Set product category meta seo during SSR for bots
 */
export async function setProductCategoryMeta() {
  const app = tryUseNuxtApp()
  const config = useRuntimeConfig()
  const route = useRoute()
  const { pageParams } = route.params
  const requestEvent = import.meta.server ? useRequestEvent() : null
  const pageUrl = listingCanonical(config.public.baseUrl, route.path, route.query.page)
  const fallbackImage = `${config.public.baseUrl}/assets/images/a_sound_effect-logo.png`
  let pageType = ''

  // default seo meta
  let name = 'Shop for Sound Effects'
  let ogType = 'website'
  let ogImage = fallbackImage
  let description = ''

  // get page seo data
  let uriSearchString = ''

  if (pageParams) {
    // The first param differentiates between software, sfx, vendors and categories
    pageType = pageParams[0]

    if ('category' === pageType) {
      // Take the last category because if we're on a subcategory page then we don't care about the parent category here.
      uriSearchString = `&category=${pageParams.findLast((categoryName) => !!categoryName)}`
    } else if ('sound-designer' === pageType) {
      uriSearchString = `&vendor=${pageParams[1]}`
    } else if ('tag' === pageType) {
      uriSearchString = `&tag=${pageParams[1]}`
    }
  }

  await useFetch(`${config.public.baseUrl}/wp-json/content-search/v1/search?type=product-category${uriSearchString}`, {
    method: 'GET'
  }).then(({ data }) => {
    const pageContent = data?.value?.['current-page']

    // Unknown category, sound designer or tag: real 404 for crawlers
    if (requestEvent && data?.value && ['category', 'sound-designer', 'tag'].includes(pageType) && ! pageContent?.name) {
      setResponseStatus(requestEvent, 404)
    }
    const robots = robotsContent(data?.value?.noindex ? { metaRobotsNoindex: 'noindex' } : {})
    name = pageContent?.name ?? name
    description = pageContent?.descriptionSeo ?? ''

    if (pageType === 'sound-designer') {
      ogType = 'profile'
    }

    if (pageContent?.image?.mediaItemUrl) {
      ogImage = pageContent?.image?.mediaItemUrl
    }

    if (! name) {
      name = 'Shop'
    }

    if (app) {
      app.runWithContext(() => {

        if (pageType === 'sound-designer') {
          // Generate and add JSON-LD structured data
          const jsonLd = generateVendorJsonLd({
            name: name,
            description: description,
            image: ogImage,
            url: pageUrl,
          })

          useHeadSafe({
            script: [{
              id: 'json-ld-vendor',
              type: 'application/ld+json',
              textContent: escapeJsonLd(jsonLd),
            }],
          })
        }

        // Yoast graph for the shop root, categories and tags (sound designers use the vendor JSON-LD above)
        if (pageType !== 'sound-designer' && data?.value?.schema) {
          useHead({
            script: [{
              id: 'json-ld',
              key: 'json-ld',
              type: 'application/ld+json',
              innerHTML: escapeJsonLd(data.value.schema),
            }],
          })
        }

        useSeoMeta({
          title: `${name} on A Sound Effect`,
          ogTitle: `${name} on A Sound Effect`,
          twitterTitle: `${name} on A Sound Effect`,
          description: description ?? '',
          ogDescription: description ?? '',
          twitterDescription: description ?? '',
          ogType: ogType ?? 'website',
          ogUrl: pageUrl,
          robots,
          ogLocale: 'en_US',
          ogSiteName: 'A Sound Effect',
          twitterSite: 'A Sound Effect',
          twitterCard: 'summary_large_image',
          ogImage: ogImage,
          twitterImage: ogImage,
          twitterImageAlt: name,
        })
      })
    }
  })
}
